import { Check, ThumbsDown, ThumbsUp, TriangleAlert, X } from 'lucide-react'
import { formatDate, formatPrice } from '../../lib/format'
import type { GiftPurchase, GiftVoteChoice, GroupMember } from '../../lib/types'

import { Badge, Button, Card, GameArt } from '../ui'

type BadgeVariant = 'neutral' | 'ember' | 'success' | 'warning' | 'danger' | 'info'

const STATUS_BADGE: Record<GiftPurchase['status'], { label: string; variant: BadgeVariant }> = {
  proposed: { label: 'Proposed', variant: 'info' },
  funded: { label: 'Funded', variant: 'success' },
  fulfilled: { label: 'Delivered', variant: 'success' },
  declined: { label: 'Declined', variant: 'neutral' },
  canceled: { label: 'Canceled', variant: 'neutral' },
}

interface GiftPurchaseCardProps {
  purchase: GiftPurchase
  /**
   * Group roster, used to resolve voter user_ids to names (§3.7 vote payloads
   * carry ids only). Unresolved voters render as "Member".
   */
  members?: GroupMember[]
  /**
   * The signed-in member's user id — picks the funded-state audience copy
   * (the recipient reads "it's in your wallet"; everyone else reads the
   * pool → wallet transfer). Display-only; no affordance keys off it.
   */
  viewerUserId?: string
  onVote: (choice: GiftVoteChoice) => void
  onCancel: () => void
  /** Chip-in shortcut for the "Pool is short" warning. */
  onChipIn?: () => void
  busy?: boolean
  className?: string
}

/**
 * One gift purchase, rendered per server status. Every affordance is gated by
 * the server's own flags (can_vote / can_cancel / pool_covers) — the client
 * adds NO lifecycle logic of its own. Funding IS the transfer: the price moves
 * pool → recipient wallet server-side, so there is no manual "send" step here.
 */
export function GiftPurchaseCard({
  purchase,
  members,
  viewerUserId,
  onVote,
  onCancel,
  onChipIn,
  busy = false,
  className,
}: GiftPurchaseCardProps) {
  const badge = STATUS_BADGE[purchase.status]
  const recipientName = purchase.recipient.display_name ?? 'the recipient'
  const gameName = purchase.game_name ?? `App ${purchase.appid}`
  const nameOf = (userId: string) =>
    members?.find((m) => m.user_id === userId)?.display_name ?? 'Member'

  return (
    <Card elevation="raised" className={className}>
      <div className="flex gap-4">
        <GameArt
          appid={purchase.appid}
          src={purchase.header_image}
          name={purchase.game_name}
          className="h-16 w-28 shrink-0"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold leading-snug text-text-strong">{gameName}</h3>
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
          <p className="text-sm text-text-muted">
            {formatPrice(purchase.price)} · a gift for{' '}
            <span className="font-medium text-text">{recipientName}</span>
          </p>
          <p className="text-xs text-text-faint">
            Proposed by {purchase.proposer.display_name ?? 'a member'}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {purchase.status === 'proposed' && (
          <>
            <p className="text-sm text-text">
              <span className="font-medium text-text-strong">
                {purchase.votes.approvals} of {purchase.votes.required}
              </span>{' '}
              approvals
            </p>
            {purchase.votes.voters.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {purchase.votes.voters.map((voter) => (
                  <Badge
                    key={voter.user_id}
                    variant={voter.vote === 'approve' ? 'success' : 'neutral'}
                    icon={
                      voter.vote === 'approve' ? (
                        <Check className="h-3.5 w-3.5" strokeWidth={2.25} />
                      ) : (
                        <X className="h-3.5 w-3.5" strokeWidth={2.25} />
                      )
                    }
                  >
                    {nameOf(voter.user_id)}
                  </Badge>
                ))}
              </div>
            )}
            {purchase.can_vote ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={purchase.my_vote === 'approve' ? 'primary' : 'secondary'}
                  onClick={() => onVote('approve')}
                  disabled={busy}
                >
                  <ThumbsUp className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                  Approve
                </Button>
                <Button size="sm" variant="secondary" onClick={() => onVote('decline')} disabled={busy}>
                  <ThumbsDown className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                  Decline
                </Button>
              </div>
            ) : (
              <p className="text-sm text-text-muted">
                The group is deciding whether to gift you this.
              </p>
            )}
            {!purchase.pool_covers && (
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-warning-fg">
                <TriangleAlert className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
                <span>Pool is short — it doesn&rsquo;t cover {formatPrice(purchase.price)} yet.</span>
                {onChipIn && (
                  <button
                    type="button"
                    onClick={onChipIn}
                    className="font-medium text-ember-300 transition hover:text-ember-400"
                  >
                    Chip in →
                  </button>
                )}
              </p>
            )}
          </>
        )}

        {purchase.status === 'funded' &&
          (viewerUserId != null && viewerUserId === purchase.recipient.user_id ? (
            <p className="text-sm text-text">
              The group gifted you {formatPrice(purchase.price)} toward {gameName} — it&rsquo;s in
              your wallet.
            </p>
          ) : (
            <p className="text-sm text-text-muted">
              {formatPrice(purchase.price)} moved from the pool to {recipientName}&rsquo;s wallet.
              We&rsquo;ll mark it delivered when their library shows the game.
            </p>
          ))}

        {purchase.status === 'fulfilled' && (
          <div className="space-y-1">
            <p className="text-sm text-success-fg">
              Delivered — {recipientName} owns it now ✓
            </p>
            {purchase.fulfilled_at && (
              <p className="text-xs text-text-faint">{formatDate(purchase.fulfilled_at)}</p>
            )}
          </div>
        )}

        {purchase.status === 'declined' && (
          <p className="text-sm text-text-muted">The group declined this one.</p>
        )}

        {purchase.status === 'canceled' && (
          <p className="text-sm text-text-muted">
            This proposal was canceled.
            {purchase.funded_at && ' The pool was refunded.'}
          </p>
        )}

        {purchase.can_cancel && (
          <div className="border-t border-border pt-3">
            <Button size="sm" variant="destructive" onClick={onCancel} disabled={busy}>
              {purchase.status === 'proposed' ? 'Cancel proposal' : 'Cancel gift'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
